import * as THREE from 'three';
import { createHouse, disposeHouseMaterials } from './props/House';
import { createTree, disposeTreeMaterials } from './props/Tree';
import { createRiver } from './props/River';
import { TrashPile, disposeTrashPileMaterial } from './props/TrashPile';
import { SmogParticles } from './props/SmogParticles';
import { BurstParticles } from './props/BurstParticles';
import { VillagerActor, disposeVillagerMaterials } from './props/Villager';
import { TIERS, tierForScore, type Tier } from './tiers';

const TRANSITION_TAU = 0.9; // seconds, exponential smoothing time constant

/** Minimal diorama whose visual state is entirely driven by a single 0-100 cleanliness score. */
export class Village {
  readonly group = new THREE.Group();

  private readonly canopyMaterial: THREE.MeshStandardMaterial;
  private readonly riverMaterial: THREE.MeshStandardMaterial;
  private readonly sun: THREE.DirectionalLight;
  private readonly trashPile: TrashPile;
  private readonly smog: SmogParticles;
  private readonly burst: BurstParticles;
  private readonly villagers: VillagerActor[] = [];

  private readonly currentFog: THREE.Color;
  private readonly currentLight: THREE.Color;
  private readonly currentTree: THREE.Color;
  private readonly currentRiver: THREE.Color;
  private currentSmogDensity: number;

  private targetTier: Tier;

  constructor(private readonly scene: THREE.Scene) {
    const startTier = tierForScore(50);
    this.targetTier = startTier;

    this.currentFog = new THREE.Color(startTier.fogColor);
    this.currentLight = new THREE.Color(startTier.lightColor);
    this.currentTree = new THREE.Color(startTier.treeColor);
    this.currentRiver = new THREE.Color(startTier.riverColor);
    this.currentSmogDensity = startTier.smogDensity;

    this.scene.background = new THREE.Color(this.currentFog.getHex());
    this.scene.fog = new THREE.Fog(this.currentFog.getHex(), startTier.fogNear, startTier.fogFar);

    this.canopyMaterial = new THREE.MeshStandardMaterial({ color: this.currentTree, roughness: 0.75 });
    this.riverMaterial = new THREE.MeshStandardMaterial({
      color: this.currentRiver,
      roughness: 0.25,
      metalness: 0.15,
    });

    const hemisphere = new THREE.HemisphereLight('#ffffff', '#3a3226', 0.6);
    this.group.add(hemisphere);

    this.sun = new THREE.DirectionalLight(this.currentLight.getHex(), startTier.lightIntensity);
    this.sun.position.set(-6, 10, 6);
    this.group.add(this.sun);

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(24, 24),
      new THREE.MeshStandardMaterial({ color: '#3d3a2c', roughness: 0.9 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.group.add(floor);

    this.group.add(createRiver(this.riverMaterial));

    const housePositions = [
      [-6, -3],
      [-3, -5.5],
      [4, -4.5],
      [7, -1.5],
    ];
    for (const [x, z] of housePositions) {
      this.group.add(createHouse(new THREE.Vector3(x, 0, z)));
    }

    const treePositions = [
      [-8, 2],
      [-5, 4],
      [-1.5, 5],
      [2, 4.5],
      [5.5, 3],
      [8, 1.5],
      [-8, -1],
      [8, -4],
    ];
    for (const [x, z] of treePositions) {
      this.group.add(createTree(new THREE.Vector3(x, 0, z), this.canopyMaterial, 0.9 + Math.random() * 0.4));
    }

    this.trashPile = new TrashPile(new THREE.Vector3(0.5, 0, 2.5));
    this.group.add(this.trashPile.group);

    this.smog = new SmogParticles();
    this.smog.points.position.y = 0.5;
    this.group.add(this.smog.points);

    this.burst = new BurstParticles();
    this.group.add(this.burst.points);

    const villagerPositions = [
      [-5.5, 0, -2.2],
      [3.7, 0, -3.8],
      [6.6, 0, -0.9],
    ];
    for (const [x, y, z] of villagerPositions) {
      const villager = new VillagerActor(new THREE.Vector3(x, y, z));
      this.villagers.push(villager);
      this.group.add(villager.group);
    }

    this.scene.add(this.group);
  }

  /** @param score cleanliness 0-100 (see ScoreController) */
  setScore(score: number): void {
    this.targetTier = tierForScore(score);
    this.trashPile.setAmount(1 - score / 100);
  }

  /** One-shot particle pop at the trash pile — fires per-answer, independent of tier boundaries. */
  triggerAnswerBurst(correct: boolean): void {
    this.burst.trigger(this.trashPile.group.position, correct);
    const villager = this.villagers[Math.floor(Math.random() * this.villagers.length)];
    if (correct) villager.reactCorrect();
    else villager.reactIncorrect();
  }

  update(delta: number): void {
    const factor = 1 - Math.exp(-delta / TRANSITION_TAU);

    this.currentFog.lerp(new THREE.Color(this.targetTier.fogColor), factor);
    this.currentLight.lerp(new THREE.Color(this.targetTier.lightColor), factor);
    this.currentTree.lerp(new THREE.Color(this.targetTier.treeColor), factor);
    this.currentRiver.lerp(new THREE.Color(this.targetTier.riverColor), factor);
    this.currentSmogDensity = THREE.MathUtils.lerp(this.currentSmogDensity, this.targetTier.smogDensity, factor);

    if (this.scene.fog instanceof THREE.Fog) {
      this.scene.fog.color.copy(this.currentFog);
      this.scene.fog.near = THREE.MathUtils.lerp(this.scene.fog.near, this.targetTier.fogNear, factor);
      this.scene.fog.far = THREE.MathUtils.lerp(this.scene.fog.far, this.targetTier.fogFar, factor);
    }
    if (this.scene.background instanceof THREE.Color) {
      this.scene.background.copy(this.currentFog);
    }

    this.sun.color.copy(this.currentLight);
    this.sun.intensity = THREE.MathUtils.lerp(this.sun.intensity, this.targetTier.lightIntensity, factor);
    this.canopyMaterial.color.copy(this.currentTree);
    this.riverMaterial.color.copy(this.currentRiver);
    this.smog.setDensity(this.currentSmogDensity);
    this.smog.update(delta);
    this.burst.update(delta);
    for (const villager of this.villagers) villager.update(delta);
  }

  currentTierLabel(): string {
    return this.targetTier.labelKo;
  }

  dispose(): void {
    this.trashPile.dispose();
    this.smog.dispose();
    this.burst.dispose();
    for (const villager of this.villagers) villager.dispose();
    this.canopyMaterial.dispose();
    this.riverMaterial.dispose();
    disposeHouseMaterials();
    disposeTreeMaterials();
    disposeTrashPileMaterial();
    disposeVillagerMaterials();
  }
}

export { TIERS };
